import {
	Component,
	ElementRef,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatStepper,
	MatStepperIcon,
	MatStep,
	MatStepLabel,
} from '@angular/material/stepper';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { SelectedCompanyRoleEvent } from 'src/app/components/assign-role/assign-role.component';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import {
	SelectedUserEvent,
	UserType,
} from 'src/app/components/user-search-bar/user-search-bar.component';
import { Company } from 'src/app/models/data/company';
import { UserProfile } from 'src/app/models/model';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { AddUserAssignRoleComponent } from './add-user-assign-role/add-user-assign-role.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { UserExistsSnackbarComponent } from '../../components/user-exists-snackbar/user-exists-snackbar.component';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AddUserAboutComponent } from './add-user-about/add-user-about.component';
import { AddUserConfirmationComponent } from './add-user-confirmation/add-user-confirmation.component';

enum StepperPage {
	about = 0,
	assignRole = 1,
	confirmation = 2,
}

@Component({
	selector: 'app-add-user-main-tab',
	templateUrl: './add-user-main-tab.component.html',
	styleUrls: ['./add-user-main-tab.component.css', '../common/stepper.scss'],
	imports: [
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStepperIcon,
		MatIcon,
		MatStep,
		MatStepLabel,
		AddUserAboutComponent,
		AddUserAssignRoleComponent,
		AddUserConfirmationComponent,
	],
})
export class AddUserMainTabComponent implements OnInit {
	@Output() addUserRequestStatusEvents =
		new EventEmitter<AddUserRequestStatusEvent>();

	@ViewChild(AddUserAssignRoleComponent)
	addUserAssignRole?: AddUserAssignRoleComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;
	snackBarRef?: MatSnackBarRef<UserExistsSnackbarComponent>;

	company?: Company;
	companyId?: string;
	@Output() userEvent?: SelectedUserEvent;
	companyRoleEvent?: SelectedCompanyRoleEvent;

	stepper!: MatStepper;

	continueDisabled = true;

	serverCallInProgress = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly route: ActivatedRoute,
		private readonly profileService: UserProfileService,
		private readonly bannerService: BannerMessageService,
		private readonly router: Router,
		private readonly navHistory: NavigationHistoryService,
		private readonly dialog: MatDialog,
		private snackBar: MatSnackBar
	) {
		this.stepper = this.stepperRef as unknown as MatStepper;
	}

	async ngOnInit(): Promise<void> {
		this.companyId =
			this.route.snapshot.queryParamMap.get('companyId') ?? undefined;
		if (this.companyId) {
			const companyResponse = await this.apiService.getCompanyById(
				this.companyId
			);
			this.company = companyResponse.company;
		}
	}

	movePrevious(stepper: MatStepper): void {
		if (stepper.selectedIndex === StepperPage.about) {
			this.navHistory.navigateBack();
		} else {
			stepper.previous();
		}
	}

	async moveNext(stepper: MatStepper): Promise<void> {
		if (stepper.selectedIndex === StepperPage.about) {
			if (!this.userEvent?.user?.id) {
				const isUserExisted = await this.checkForExactEmailMatch();
				if (isUserExisted) {
					this.snackBarRef = this.snackBar.openFromComponent(
						UserExistsSnackbarComponent,
						{
							duration: 3000,
							panelClass: 'existing-user-snackbar',
						}
					);
					return;
				}
			}

			if (this.userEvent?.user?.id) {
				const isUserWhiteListed =
					await this.apiService.getUserWhitelistedStatus(
						this.userEvent.user.id
					);
				if (isUserWhiteListed) {
					this.dialog.open(CancelConfirmationComponent, {
						panelClass: 'styled-dialog-container',
						maxWidth: '810px',
						minHeight: '268px',
						data: {
							dialogHeader: $localize`:@@userIsWhitelisted-confirmationHeader:User Whitelist Restriction`,
							dialogContent: $localize`:@@userIsWhitelisted-confirmationContext:This user is currently whitelisted with another company and cannot be added here. To proceed, please have the user's whitelist disabled from their current company first.`,
							dialogSecondaryButtonText: 'Ok',
							showDevider: true,
						},
					});
					return;
				}
			}
			stepper.next();
		} else {
			this.checkForExistingRoleAndDoAdds(stepper);
		}
	}

	onSelectedUserEvent(event: SelectedUserEvent): void {
		// This if/else was put here to fix an ExpressionChangedAfterItHasBeenCheckedError
		if (event.user.email !== '') {
			this.userEvent = event;

			this.continueDisabled =
				this.userEvent?.userType === UserType.newUser &&
				(!this.userEvent.emailValid ||
					this.userEvent.user.firstName.length === 0 ||
					this.userEvent.user.lastName.length === 0);
		} else {
			this.continueDisabled = true;
		}
	}

	onSelectedCompanyRoleEvent(event: SelectedCompanyRoleEvent): void {
		this.companyRoleEvent = event;
	}

	async addUserAndRole(): Promise<void> {
		this.addUserRequestStatusEvents.emit({
			status: AddUserRequestStatus.loading,
		});
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		const parentId: string | undefined =
			this.companyId ?? userProfile?.activeRoleDetails?.company.id;

		if (parentId === undefined) {
			// todo show some error
			return;
		}

		const user = this.userEvent?.user;
		const company = this.companyRoleEvent?.company;
		const newRoleInformation = this.companyRoleEvent?.newRoleInformation;
		let role = this.companyRoleEvent?.role;

		try {
			// if user and role exist, assign role
			if (
				user !== undefined &&
				this.userEvent?.userType !== UserType.newUser &&
				role !== undefined &&
				company !== undefined
			) {
				await this.apiService.assignRoleToUserId(
					[user.id],
					role.id,
					company.id
				);
				this.addUserRequestStatusEvents.emit({
					status: AddUserRequestStatus.success,
					data: {
						userName: `${user.firstName} ${user.lastName}`,
						userEmail: user.email,
						roleName: role.name,
						companyName: company.name,
					},
				});
			} else if (
				user !== undefined &&
				newRoleInformation !== undefined &&
				company !== undefined &&
				this.userEvent?.userType === UserType.existingUser
			) {
				// if user exist and role does not, create and assign role to user
				const newRole = await this.apiService.createNewRole(
					company.id,
					newRoleInformation.name,
					newRoleInformation.systemCapabilityItems
				);
				await this.apiService.assignRoleToUserId(
					[user.id],
					newRole.id,
					company.id
				);

				this.addUserRequestStatusEvents.emit({
					status: AddUserRequestStatus.success,
					data: {
						userName: `${user.firstName} ${user.lastName}`,
						userEmail: user.email,
						roleName: newRole.name,
						companyName: company.name,
					},
				});
			}

			if (user?.id === (await this.profileService.getUserProfile()).user.id) {
				this.profileService.clearUserProfile();
				await this.profileService.getUserProfile();
			}

			// If user doesnt exist register new user and pass in parent entity Id and role object newly created or existing
			if (this.userEvent?.userType === UserType.newUser) {
				let userId: string[] = [];

				if (newRoleInformation !== undefined) {
					const newUserResponse = await this.apiService.registerNewUser(
						[this.userEvent.user],
						parentId,
						role?.id,
						newRoleInformation.name,
						newRoleInformation.systemCapabilityItems
					);

					userId = newUserResponse.data.userIds;
					role = newUserResponse.data.role;
				} else {
					const newUserResponse = await this.apiService.registerNewUser(
						[this.userEvent.user],
						parentId,
						role?.id
					);

					userId = newUserResponse.data.userIds;
				}
				this.userEvent.user.id = userId[0];

				if (user !== undefined && role !== undefined && company !== undefined) {
					this.addUserRequestStatusEvents.emit({
						status: AddUserRequestStatus.success,
						data: {
							userName: `${user.firstName} ${user.lastName}`,
							userEmail: user.email,
							roleName: role.name,
							companyName: company.name,
						},
					});
				}
			}
		} catch (error) {
			const userEmail = this.userEvent?.user.email ?? '';
			const companyName = this.companyRoleEvent?.company?.name ?? '';
			const roleName = this.companyRoleEvent?.role?.name ?? '';

			this.addUserRequestStatusEvents.emit({
				status: AddUserRequestStatus.failure,
				data: { userEmail, companyName, roleName },
			});
		}
	}

	private async checkForExactEmailMatch(): Promise<boolean> {
		const normalizedEmail =
			this.userEvent?.user?.email?.trim().toLowerCase() ?? '';

		const queryParameters: QueryParam[] = [
			{ param: 'email', value: normalizedEmail },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		const users = await this.apiService.getAllUsers(queryParameters);
		const existingUser = users.find(
			(user) => user.email?.trim()?.toLowerCase() === normalizedEmail
		);
		return !!existingUser;
	}

	async checkForExistingRoleAndDoAdds(stepper: MatStepper): Promise<void> {
		// make sure the user can't create the record multiple times from clicking the button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;

			let selectedCompanyId = this.companyRoleEvent?.company?.id;
			if (!selectedCompanyId) {
				selectedCompanyId = (await this.profileService.getUserProfile())
					.activeRoleDetails?.company.id;
			}

			if (!selectedCompanyId) {
				console.error('No company selected!');
				this.bannerService.sendMessage(
					'Error adding User or Role!',
					BannerMessageType.error
				);
				return;
			}

			const userIds: string[] = [];
			if (this.userEvent?.user?.id) {
				userIds.push(this.userEvent?.user.id);
			}

			let userRolesExist = false;

			if (userIds.length > 0) {
				const queryParameters: QueryParam[] = [
					{ param: 'userIds', value: userIds },
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 1 },
				];
				const userProfiles = await this.apiService.getCompanyUsersByCompanyId(
					selectedCompanyId!,
					queryParameters
				);
				userRolesExist = userProfiles.length > 0;
			}

			if (userRolesExist) {
				this.dialog.open(CancelConfirmationComponent, {
					data: {
						dialogHeader: $localize`:@@addUserAssignRole-confirmationHeader:Warning: User already part of company.`,
						dialogContent: $localize`:@@addUserAssignRole-confirmationContext:This user already as a role in your company.  Continuing will reassign the user to role selected.`,
						dialogPrimaryButtonText: 'Cancel',
						dialogSecondaryButtonText: 'Continue',
						secondaryFunction: () => {
							this.addUserAndRole();
							stepper.next();
						},
					},
				});
			} else {
				this.addUserAndRole();
				stepper.next();
			}

			this.serverCallInProgress = false;
		}
	}

	isStepOneButtonIsAbleToContinue() {
		return (
			this.companyRoleEvent &&
			this.companyRoleEvent.company &&
			((this.companyRoleEvent.role && this.companyRoleEvent.role.id !== '') ||
				(this.companyRoleEvent.newRoleInformation &&
					this.companyRoleEvent.newRoleInformation.name.trim() !== ''))
		);
	}
}

export enum AddUserRequestStatus {
	loading,
	success,
	failure,
}

export interface AddUserRequestStatusEvent {
	status: AddUserRequestStatus;
	data?: {
		userName?: string;
		userEmail: string;
		companyName: string;
		roleName: string;
	};
}
